import { Link } from 'react-router-dom';
import { ArrowRight, DollarSign, Calculator, Users, FileText, Search, Megaphone } from 'lucide-react';
import useSEO from '../hooks/useSEO';

const SERVICES = [
  {
    icon: DollarSign, slug: 'price-estimator', title: 'Aircraft Price Estimator',
    desc: 'Scopri il valore reale del tuo velivolo. Il nostro strumento analizza dati di mercato, ore di volo, anno di costruzione e condizioni per fornirti una stima accurata e aggiornata. Ideale per venditori che vogliono posizionarsi al prezzo giusto.',
    cta: 'Valuta il tuo velivolo', highlight: true, hasPage: true, beta: true,
  },
  {
    icon: Calculator, slug: 'cost-calculator', title: 'Operating Cost Calculator',
    desc: 'Calcola i costi operativi reali prima di acquistare. Carburante, manutenzione, assicurazione, hangaraggio e revisioni: ottieni una panoramica completa dei costi annuali per ogni tipo di velivolo. Lo strumento perfetto per acquirenti seri.',
    cta: 'Calcola i costi', highlight: true, hasPage: true, beta: true,
  },
  {
    icon: Users, slug: 'consulenze', title: 'Consulenze',
    desc: 'Supporto professionale per ogni fase della compravendita, dall\'analisi del velivolo alla strategià di negoziazione. Le nostre consulenze aiutano a prendere decisioni rapide e sicure, evitando errori costosi.',
    hasPage: true, customPath: '/consulenze-compravendita-velivoli',
  },
  {
    icon: FileText, slug: 'lettera-intenti', title: 'Lettera di Intenti',
    desc: 'Formalizza in modo semplice e sicuro l\'intenzione di acquistare o vendere un velivolo. Definisci prezzo, condizioni e periodi di esclusiva prima della due diligence, tutelando entrambe le parti.',
    hasPage: true, customPath: '/lettera-di-intenti-compravendita-velivolo-tra-privati',
  },
  {
    icon: Search, slug: 'piani-acquisizione', title: 'Piani di Acquisizione',
    desc: 'Analizziamo il mercato e definiamo una strategià su misura per l\'acquisto del tuo velivolo. I Piani di Acquisizione aiutano a identificare opportunità, valutare il prezzo giusto e ridurre i rischi.',
    hasPage: true, customPath: '/piano-di-acquisizione-velivolo',
  },
  {
    icon: Megaphone, slug: 'spazio-pubblicitario', title: 'Spazio Pubblicitario',
    desc: 'Promuovi la tua azienda con banner premium in homepage per ottenere massima visibilità nel settore aeronautico.',
    hasPage: true, customPath: '/spazio-pubblicitario',
  },
];

export default function Services() {
  useSEO({
    title: 'Servizi Premium per l\'Aviazione — Consulenze, Valutazioni, LOI',
    description: 'Servizi professionali AeroDeals: consulenza acquisto/vendita, valutazione aeromobili, piano di acquisizione, lettera di intenti (LOI) e spazio pubblicitario per operatori del settore.',
    keywords: 'consulenza acquisto aereo, valutazione aeromobile, lettera di intenti aereo, LOI, servizi aviazione',
  });
  return (
    <div className="min-h-screen bg-[#09090B] pt-20" data-testid="services-page">
      {/* Hero */}
      <section className="py-20 border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-4">Servizi</p>
            <h1 className="text-4xl md:text-5xl tracking-tighter font-medium text-white mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Servizi Premium<br />per l'Aviazione
            </h1>
            <p className="text-lg text-zinc-400 leading-relaxed">
              Tutto ciò di cui hai bisogno per comprare o vendere il tuo velivolo con sicurezza e professionalità.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-10">I Nostri Servizi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {SERVICES.map(s => {
              const Wrapper = s.hasPage ? Link : 'div';
              const wrapperProps = s.hasPage ? { to: s.customPath || `/services/${s.slug}` } : {};
              return (
                <Wrapper
                  key={s.slug} {...wrapperProps}
                  className={`border p-8 transition-all group block ${s.highlight ? 'bg-[#C5A059]/[0.04] border-[#C5A059]/20 hover:border-[#C5A059]/50' : 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-600'}`}
                  data-testid={`service-${s.slug}`}
                >
                  <div className={`w-12 h-12 flex items-center justify-center mb-5 ${s.highlight ? 'bg-[#C5A059]/15' : 'bg-zinc-800'}`}>
                    <s.icon size={22} className="text-[#C5A059]" />
                  </div>
                  <h3 className="text-xl font-medium text-white mb-3 group-hover:text-[#C5A059] transition-colors flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    {s.title}
                    {s.beta && <span className="text-[9px] font-mono uppercase tracking-widest bg-[#C5A059]/20 text-[#C5A059] px-2 py-0.5 rounded-sm">Beta</span>}
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed mb-5">{s.desc}</p>
                  <span className={`inline-flex items-center gap-1.5 text-xs transition-colors ${s.highlight ? 'bg-[#C5A059] text-black px-4 py-2 font-medium hover:bg-[#D4B271]' : 'text-[#C5A059]'}`}>
                    {s.cta || 'Scopri di piu'} <ArrowRight size={12} />
                  </span>
                </Wrapper>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
